import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Tab, styled } from "@mui/material";
import { toast } from "react-toastify";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { AppThunkDispatch, RootState } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { EditMyAccountRef } from "../EditMyAccount";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { Document, DocumentMasterTabRef } from "./DocumentMasterTab";
import { useNavigate } from "react-router-dom";
import {
  DocumentMaster,
  DocumentMasterResponse,
} from "./T4OnBoardingDocument/UploadOnBoardingDocument/TabContent";
import TabContent from "./T4OnBoardingDocument/UploadOnBoardingDocument/TabContent";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { DocumentApprovalStatus } from "@/constants/enums/document-approval-status.enum";
import { OnboardingDocumentMasterType } from "@/constants/enums/onboarding-document-master-types.enum";
import { useOnboardingDocumentModuleContext } from "./Provider/OnboardingDocumentModuleProvider";

// Styled components
const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

interface DocumentsProps {
  isView?: boolean;
  userId?: string;
  isTimeline?: boolean;
  isApprovedView?: boolean;
}

const Documents: React.FC<DocumentsProps> = ({
  isView = false,
  userId,
  isTimeline = false,
  isApprovedView = false,
}) => {
  const activeTabQuery = new URLSearchParams(window.location.search).get(
    "activeTab",
  );
  const activeFinOBQuery = new URLSearchParams(window.location.search).get(
    "activeFinOB",
  );

  const dispatch = useDispatch<AppThunkDispatch>();
  const [activeTab, setActiveTab] = useState(
    activeTabQuery ? parseInt(activeTabQuery) : 0,
  );
  const [activeFinOB, setActiveFinOB] = useState(
    activeFinOBQuery ? parseInt(activeFinOBQuery) : 0,
  );
  const [isManualFinOBTabChange, setIsManualFinOBTabChange] = useState(false);
  const [completedTabs, setCompletedTabs] = useState<number[]>([-1]);
  const [documentMaster, setDocumentMaster] = useState<
    DocumentMasterResponse["data"] | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const formRef = useRef<EditMyAccountRef>(null);
  const documentMasterTabRef = useRef<DocumentMasterTabRef>(null);
  const [forceRefresh, setForceRefresh] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const { setIsApprovedView } = useOnboardingDocumentModuleContext();
  const [clientName, setClientName] = useState<string>("");

  useEffect(() => {
    setActiveTab(activeTabQuery ? parseInt(activeTabQuery) : 0);
    setActiveFinOB(activeFinOBQuery ? parseInt(activeFinOBQuery) : 0);
    fetchDocumentMaster();
  }, []);

  useEffect(() => {
    if (isApprovedView) {
      setIsApprovedView(true);
    }
  }, [isApprovedView]);

  useEffect(() => {
    handleResettingFinOBTab();
  }, [activeTab, isManualFinOBTabChange]);

  useEffect(() => {
    if (activeTab === 0 && activeFinOB === 0) {
      handleValidatingProfile();
    }
    const from = new URLSearchParams(window.location.search).get("from");
    navigate(
      `${window.location.pathname}?activeTab=${activeTab}&activeFinOB=${activeFinOB}${from ? `&from=${from}` : ""}`,
    );
  }, [activeFinOB, activeTab]);

  const handleMyDrive = () => {
    navigate("/document/my-drive");
  };

  const handleSettingCompletedTabs = async () => {
    if (documentMaster) {
      if (isView) {
        setCompletedTabs(
          documentMaster?.[activeTab === 0 ? "financial" : "legal"].map(
            (_doc, index) => index,
          ),
        );
      } else {
        const individualCompletedTabs: number[] = [];
        if (
          activeTab === 0 &&
          (activeFinOB === 0 ? await handleValidatingProfile(true) : true)
        ) {
          individualCompletedTabs.push(0);
        }
        setCompletedTabs([
          ...individualCompletedTabs,
          ...calculateProgressIndividual(
            activeTab === 0 ? "financial" : "legal",
          ).individualCompletedTabs,
        ]);
      }
    }
  };

  useEffect(() => {
    handleSettingCompletedTabs();
  }, [documentMaster, activeTab]);

  useEffect(() => {
    if (forceRefresh) {
      fetchDocumentMaster();
      setForceRefresh(false);
    }
  }, [forceRefresh]);

  const handleResettingFinOBTab = useCallback(() => {
    if (isManualFinOBTabChange) {
      setIsManualFinOBTabChange(false);
      setActiveFinOB(0);
    }
  }, [activeTab, isManualFinOBTabChange]);

  const handleValidatingProfile = (returnData?: boolean) => {
    return new Promise(async (resolve) => {
      let isFormValid = false;
      if (
        formRef.current?.formik &&
        Object.keys((await formRef.current?.formik.validateForm()) || {})
          .length == 0
      ) {
        if (!returnData) {
          setCompletedTabs([...completedTabs, 0]);
        }
        isFormValid = true;
      }
      resolve(isFormValid);
    });
  };

  const convertDocumentMasterObjectToArray = (obj: any) => {
    return Object.values(obj).sort((a: any, b: any) => {
      const aSequence = a.sequence || a.title.slice(-2);
      const bSequence = b.sequence || b.title.slice(-2);
      return aSequence - bSequence;
    }) as DocumentMaster[];
  };

  const fetchDocumentMaster = async () => {
    try {
      const { authenticatedCall } = actionsHandler(
        isView ? "viewDocumentMaster" : "documentMaster",
      );
      const id = !userId ? user.id : userId;
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: isView ? [id] : undefined,
        }),
      );

      if (response.ok) {
        setClientName(response.data.data.client_name || "");
        const tabs = {
          financial: convertDocumentMasterObjectToArray(
            response.data.data.financial,
          ),
          legal: convertDocumentMasterObjectToArray(response.data.data.legal),
        };

        // Use API data if available, otherwise use static data for FIN OB 1
        const finOb1APIData = tabs.financial.find(
          (doc) => doc.id === OnboardingDocumentMasterType.finOb1,
        );
        const finOb1Data = {
          ...{
            description: "Company Info (FIN OB 1)",
            documents: [
              {
                id: 1,
                status: DocumentApprovalStatus.pending,
                document_name: "Company Info",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                uploaded_document: null,
              },
            ],
            folder_id: 0,
            id: OnboardingDocumentMasterType.finOb1,
            sequence: 1,
            title: "FIN OB 1",
          },
          ...(finOb1APIData || {}),
        } as DocumentMaster;

        tabs.financial = [
          ...[finOb1Data],
          ...tabs.financial.filter(
            (doc) => doc.id !== OnboardingDocumentMasterType.finOb1,
          ),
        ];

        setDocumentMaster(tabs);
      } else {
        toast.error("Failed to fetch documents");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Unable to load documents");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setIsManualFinOBTabChange(true);
    setActiveTab(newValue);
  };

  const handleFinOBChange = async (index: number) => {
    if (!completedTabs.includes(index)) {
      if (
        !(activeTab === 0 && activeFinOB === 0) &&
        !documentMasterTabRef.current?.validateDocuments()
      ) {
        return;
      }
      if (activeTab === 0 && activeFinOB === 0) {
        if (
          formRef.current?.formik &&
          Object.keys((await formRef.current?.formik.validateForm()) || {})
            .length !== 0
        ) {
          toast.warning("Please fill all the required fields");
          return;
        }
      }
    }

    setActiveFinOB(index);
  };

  const handleButtonClick = () => {
    if (activeTab === 0 && activeFinOB === 0) {
      formRef.current?.formik.handleSubmit();
    } else {
      validateAndProceed();
    }
  };

  const validateAndProceed = () => {
    if (documentMasterTabRef.current?.validateDocuments()) {
      nextTab();
    }
  };

  const nextTab = () => {
    setActiveFinOB(activeFinOB + 1);
    if (!completedTabs.includes(activeFinOB)) {
      setCompletedTabs([...completedTabs, activeFinOB]);
    }
  };

  const calculationLogic = (doc: Document) => {
    const isValid = isView
      ? doc.uploaded_document?.status === DocumentApprovalStatus.approved
      : Boolean(doc.uploaded_document?.id || doc.uploaded_document?.mark_as_na);
    return isValid;
  };

  const calculateProgressTotal = () => {
    const tabDocs = { ...documentMaster };
    const totalDocs = Object.values(tabDocs).reduce((acc, curr) => {
      return (
        acc +
        curr.reduce(
          (acc, curr) =>
            curr.id !== OnboardingDocumentMasterType.finOb1
              ? acc + curr.documents.length
              : acc,
          0,
        )
      );
    }, 0);
    // You can add logic here to calculate completed documents
    const completedDocs =
      calculateProgressIndividual("financial").completed +
      calculateProgressIndividual("legal").completed;
    return { total: totalDocs, completed: completedDocs };
  };

  const calculateProgressIndividual = useCallback(
    (type: "financial" | "legal") => {
      const documents = [...(documentMaster?.[type] || [])].filter(
        (doc) => doc.id !== OnboardingDocumentMasterType.finOb1,
      );
      const totalDocs = [...documents].reduce(
        (acc, curr) => acc + curr.documents.length,
        0,
      );
      const individualCompletedTabs: number[] = [];
      const completedDocs = [...documents].reduce((acc, curr, index) => {
        const completed = curr.documents.filter((doc) =>
          calculationLogic(doc),
        ).length;

        if (completed === curr.documents.length) {
          individualCompletedTabs.push(
            type === "financial" ? index + 1 : index,
          );
        }
        const individualCompleted = acc + completed;
        return individualCompleted;
      }, 0);

      return {
        total: totalDocs,
        completed: completedDocs,
        individualCompletedTabs,
      };
    },
    [documentMaster, activeTab],
  );

  const totalProgressText = () => {
    return (
      <>
        {calculateProgressTotal().completed} out of{" "}
        {calculateProgressTotal().total} Doc
      </>
    );
  };

  const individualProgressText = (type: "financial" | "legal") => {
    return (
      <>
        {calculateProgressIndividual(type).completed} out of{" "}
        {calculateProgressIndividual(type).total} Doc
      </>
    );
  };

  const handleFinalSubmitClick = async () => {
    try {
      if (
        calculateProgressTotal().completed !== calculateProgressTotal().total
      ) {
        toast.warning(
          "All mandatory documents marked with * must be uploaded or marked as Not Applicable.",
        );
        return;
      }

      const { authenticatedCall } = actionsHandler("onBoardingSubmit");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {},
        }),
      );

      if (response.ok) {
        toast.success(
          response.data.message || "Documents submitted successfully",
        );
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error submitting documents:", error);
      toast.error("Failed to submit documents");
    }
  };

  const isNotLastTab = useCallback(() => {
    return (
      activeFinOB !==
      (documentMaster?.[activeTab === 0 ? "financial" : "legal"].length || 0) -
        1
    );
  }, [activeTab, activeFinOB, documentMaster]);

  const getTabClass = useCallback(
    (index: number) => {
      if (!isView) {
        return completedTabs.includes(index)
          ? "!tw-text-[#0F766E] !tw-bg-[#F0FDF9] !tw-font-semibold !tw-opacity-100"
          : "!tw-text-[#B54708] !tw-bg-[#FFF5F4] !tw-font-semibold !tw-opacity-100";
      }
      return "";
    },
    [completedTabs, isView],
  );

  const handleBackClick = () => {
    const from = new URLSearchParams(window.location.search).get("from");
    console.log("from: ", from);
    if (from) {
      navigate(from);
    } else {
      navigate("/dashboard");
    }
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <CustomLoader loading={true} isWrapper={true} size="50" />
      </div>
    );
  }

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[100px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white">
          {/* Header content */}
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-w-[60%]">
              <div className="tw-flex tw-items-start tw-flex-col">
                <div className="tw-flex tw-items-center">
                  <a onClick={handleBackClick}>
                    <img src={BackIcon} alt="Back Icon" />
                  </a>
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold font-primary-color tw-cursor-pointer">
                    {isView}{" "}
                    {user.user_type.id === UserTierType.Tier4
                      ? (isTimeline && "Files Timeline") ||
                        (isView && "On-boarding Document") ||
                        "Upload Documents"
                      : "Uploaded Documents"}
                  </h1>
                </div>
                {user.user_type.id === UserTierType.Tier4 && !isTimeline ? (
                  <div className="tw-text-sm tw-text-gray-600 tw-mt-2">
                    Upload below documents for profile verification. Supported
                    Format: PDF, Excel, CSV, Docx, PNG, JPG, SVG (25mb each)
                  </div>
                ) : undefined}
              </div>
            </div>

            {/* Progress section */}
            {documentMaster && (
              <div className="tw-w-[35%] tw-flex tw-justify-end">
                {user?.user_type?.id !== UserTierType.Tier3 && (
                  <div className="tw-flex tw-items-center tw-gap-4 tw-justify-center">
                    {!isTimeline && isView && (
                      <button
                        onClick={handleMyDrive}
                        className="tw-flex tw-justify-center tw-text-center tw-border-2 tw-border-[#2889eb] tw-border-primary tw-rounded-md tw-px-4 tw-py-2 bg-transparent tw-text-[#146C94]"
                      >
                        My Drive
                      </button>
                    )}
                    <div className="tw-bg-white tw-rounded-lg tw-w-fit tw-border tw-border-[#E9EAEB] tw-p-2">
                      <h2 className="tw-text-[12px] tw-font-medium font-primary-color tw-mb-2">
                        Files {isView ? "approved" : "uploaded"}
                      </h2>
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-w-[200px] tw-h-1 tw-bg-[#E5E7EB] tw-rounded-full">
                          <div
                            className="tw-h-full tw-bg-[#146C94] tw-rounded-full"
                            style={{
                              width: `${(calculateProgressTotal().completed / calculateProgressTotal().total) * 100}%`,
                            }}
                          />
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-between">
                          <span className="tw-text-[#809199] tw-text-[10px]">
                            {totalProgressText()}
                          </span>
                          <span className="tw-text-[#146C94] tw-text-[10px] tw-font-medium">
                            {(
                              (calculateProgressTotal().completed /
                                calculateProgressTotal().total) *
                              100
                            ).toFixed(2)}
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                    {!isView && (
                      <button
                        onClick={handleFinalSubmitClick}
                        className="tw-flex tw-justify-center tw-text-center tw-mx-auto tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                      >
                        Submit Documents
                      </button>
                    )}
                  </div>
                )}
                {user?.user_type?.id === UserTierType.Tier3 && (
                  <div className="tw-flex tw-items-center tw-bg-[#F8FAFC] tw-gap-2 tw-border tw-rounded-full tw-px-4 tw-py-2">
                    <div className="tw-text-sm tw-text-[#809199] tw-font-medium">
                      Client{" "}
                    </div>
                    <div className="tw-text-sm tw-font-semibold tw-text-[#232323]">
                      {clientName}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`${user.user_type.id === UserTierType.Tier4 ? "tw-bg-white" : "tw-bg-[#F8FAFC] tw-border tw-border-[#E9EAEB]"} tw-rounded-lg tw-px-8 tw-pt-4 tw-pb-4`}
      >
        <div
          className={`${!isTimeline && user?.user_type?.id !== UserTierType.Tier3 ? "tw-mt-[180px]" : "tw-mt-[150px]"} tw-bg-white tw-rounded-lg ${user.user_type.id === UserTierType.Tier3 ? "tw-border tw-border-[#E9EAEB]" : ""}`}
        >
          {/* Main Tabs */}

          <div className="tw-border-b tw-border-[#E9EAEB] tw-px-4 tw-pt-2">
            <div className="tw-w-fit tw-mt-2 tw-border tw-border-[#E9EAEB]">
              <StyledTabs value={activeTab} onChange={handleTabChange}>
                <StyledTab
                  label="Financial"
                  className={`tw-text-sm tw-font-semibold ${
                    activeTab === 0 ? "active-tab" : "font-primary-color"
                  } tab-border`}
                />
                <StyledTab
                  label="Legal"
                  className={`tw-text-sm tw-font-semibold ${
                    activeTab === 1 ? "active-tab" : "font-primary-color"
                  } tab-border`}
                />
              </StyledTabs>
            </div>
          </div>

          {user.user_type.id === UserTierType.Tier4 && !isTimeline && (
            <>
              {/* Progress Bar */}
              <div className="progress-bar tw-px-4 tw-py-2 tw-mt-3">
                {documentMaster && (
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                    <div className="tw-w-[calc(100%-100px)] tw-h-2 tw-bg-[#E2E8F0] tw-rounded-full">
                      <div
                        className="tw-h-full tw-bg-[#027A48] tw-rounded-full"
                        style={{
                          width: `${(calculateProgressIndividual(activeTab === 0 ? "financial" : "legal").completed / calculateProgressIndividual(activeTab === 0 ? "financial" : "legal").total) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="tw-text-[#475569] tw-text-sm tw-font-semibold tw-top-0 tw-right-4 tw-min-w-[110px]">
                      {individualProgressText(
                        activeTab === 0 ? "financial" : "legal",
                      )}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Content Area */}
          <div className="tw-px-4 tw-py-4">
            {documentMaster && (
              <div className="custom-card !tw-bg-white">
                {/* Second Level Tabs */}
                <div className="custom-card-header !tw-pb-0">
                  <div className="tw-overflow-x-auto tw-border tw-border-[#E9EAEB] tw-w-fit">
                    {(activeTab === 0
                      ? documentMaster.financial
                      : documentMaster.legal
                    ).map((section, index) => (
                      <StyledTab
                        key={section.title + section.id}
                        label={section.title}
                        onClick={() => handleFinOBChange(index)}
                        className={`tw-text-sm tw-font-semibold !tw-px-4 !tw-py-2 ${
                          index === activeFinOB
                            ? "active-tab !tw-opacity-100"
                            : getTabClass(index)
                        } tab-border`}
                        disabled={
                          index !== 0 && !completedTabs.includes(index - 1)
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Content Based on Selected Tabs */}
                <div className="custom-card-body">
                  <TabContent
                    userId={userId}
                    activeTab={activeTab}
                    activeFinOB={activeFinOB}
                    documentMaster={documentMaster}
                    formRef={formRef}
                    documentMasterTabRef={documentMasterTabRef}
                    forceRefresh={forceRefresh}
                    setForceRefresh={setForceRefresh}
                    nextTab={nextTab}
                    handleValidatingProfile={handleValidatingProfile}
                    isTableView={isView}
                    isTimeline={isTimeline}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        {!isView && isNotLastTab() && (
          <button
            onClick={handleButtonClick}
            className="tw-flex tw-justify-center tw-text-center tw-mx-auto tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button tw-mt-2"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default Documents;
